export type PortfolioCategory = '웹' | '앱' | 'UX/UI' | '퍼블리싱' | '콘텐츠 디자인';

export type PortfolioProject = {
  slug: string;
  title: string;
  subtitle: string;
  client: string;
  categories: PortfolioCategory[];
  scope: string;
  date: string;
  thumbnail: string | null;
  thumbnailFit?: 'cover' | 'contain';
  description: string;
  background: string;
  outcome: string;
  featured: boolean;
  tasks: string[];
  tools?: string[];
  screens?: string[];
  legacy?: boolean;
};

export const portfolioProjects: PortfolioProject[] = [
  {
    slug: 'enterprise-service-landing-renewal',
    title: '대기업 주요 서비스 랜딩페이지 리뉴얼',
    subtitle: '기업 주요 서비스 정보를 명확하게 전달하는 웹 경험 개선',
    client: '국내 대기업 IT 계열',
    categories: ['웹', 'UX/UI', '퍼블리싱'],
    scope: '기업 홈페이지 · UX/UI · 반응형 웹 · 퍼블리싱',
    date: '2026-07-01',
    thumbnail: '/images/portfolio/enterprise-service-landing-renewal.png',
    thumbnailFit: 'contain',
    description: '기업의 주요 서비스와 기술 정보를 사용자가 쉽고 명확하게 이해할 수 있도록 랜딩페이지 구조와 사용자 경험 전반을 개선했습니다.',
    background: '다양한 주요 서비스와 기술 정보를 하나의 기업 홈페이지 안에서 일관성 있게 전달해야 하는 프로젝트였습니다. 기존 브랜드 아이덴티티는 유지하면서도 서비스별 특성을 명확하게 구분할 수 있도록 정보구조와 화면 체계를 재정비했습니다.',
    outcome: '복잡한 기술 정보를 방문자가 빠르게 탐색할 수 있도록 콘텐츠 흐름을 정리하고, 서비스별 페이지의 시각적 완성도와 반응형 사용성을 개선했습니다.',
    featured: true,
    tasks: ['메인 페이지 및 주요 서비스 페이지 UX/UI 설계', 'AI, 제조, 클라우드, 보안 등 기술 콘텐츠 구조화', '기업 브랜드 아이덴티티 기반 인터페이스 개선', '전체 정보구조 및 콘텐츠 위계 재설계', 'PC, 태블릿, 모바일 반응형 화면 설계', '공통 컴포넌트 및 디자인 시스템 구축', '퍼블리싱 구조 설계 및 화면 품질 고도화', '기획, 디자인, 개발 조직 간 협업'],
    tools: ['Figma', 'Responsive Web', 'Publishing'],
  },
  {
    slug: 'ai-technology-content-visualization',
    title: 'AI 기술 콘텐츠 시각화 디자인',
    subtitle: '복잡한 AI 기술 정보를 쉽게 전달하는 콘텐츠 디자인',
    client: '국내 AI·소프트웨어 기술 기업',
    categories: ['콘텐츠 디자인'],
    scope: '콘텐츠 디자인 · 인포그래픽 · 썸네일',
    date: '2026-04-01',
    thumbnail: '/images/portfolio/ai-technology-content-visualization.png',
    thumbnailFit: 'contain',
    description: '복잡한 AI와 데이터 기술을 일반 사용자도 쉽게 이해할 수 있도록 그래픽 콘텐츠와 인포그래픽으로 시각화했습니다.',
    background: 'AI, OCR, 데이터 파싱, 문서 처리처럼 설명 난도가 높은 기술 정보를 콘텐츠 독자가 직관적으로 이해할 수 있도록 재구성하는 프로젝트였습니다. 기술적 정확성과 기업 브랜드의 시각적 일관성을 함께 유지하는 것이 중요했습니다.',
    outcome: '기술 중심 콘텐츠를 보다 쉽고 친숙하게 전달할 수 있도록 개선하고, 다양한 채널에서 일관성 있게 사용할 수 있는 콘텐츠 디자인 체계를 구축했습니다.',
    featured: true,
    tasks: ['AI 및 데이터 기술 콘텐츠 분석', '복잡한 기술 정보의 시각적 구조화', '콘텐츠 마케팅용 썸네일 디자인', '기술 설명용 인포그래픽 제작', '기업 브랜드 가이드 기반 그래픽 디자인', '반복 제작을 위한 디자인 템플릿 구축', 'Figma 기반 컴포넌트 및 디자인 시스템 관리'],
    tools: ['Figma', 'Content Design', 'Infographic'],
  },
  {
    slug: 'ai-based-diagnostic-service',
    title: 'AI 기반 맞춤형 진단 서비스',
    subtitle: '질문과 결과 해석에 집중한 진단형 서비스 UX/UI',
    client: 'AI 서비스 기업',
    categories: ['웹', 'UX/UI'],
    scope: 'AI 서비스 · 웹 기획 · UX/UI',
    date: '2025-10-01',
    thumbnail: '/images/portfolio/ai-based-diagnostic-service.png',
    thumbnailFit: 'contain',
    description: '사용자의 응답과 조건을 분석해 개인화된 결과를 제공하는 진단형 AI 웹 서비스를 기획하고 디자인했습니다.',
    background: '사용자가 여러 질문을 거쳐 입력한 상태와 조건에 맞는 결과를 안내받는 서비스였습니다. 진단 과정에서 이탈하지 않도록 흐름을 단순화하고, 최종 결과를 쉽게 해석할 수 있는 정보 전달 구조를 설계했습니다.',
    outcome: '복잡한 진단 과정을 단계별로 분리해 입력 부담을 줄이고, 결과 이해도를 높이는 서비스 흐름을 구축했습니다.',
    featured: true,
    tasks: ['AI 진단형 웹 서비스 기획', '질문, 응답, 결과 화면의 전체 흐름 설계', '단계별 진행 방식 및 사용자 피드백 설계', '진단 과정 정보구조 정의', '결과 해석 중심 UX/UI 디자인', '콘텐츠 우선순위 및 시각적 위계 구성', '모바일 환경을 고려한 화면 설계'],
    tools: ['Figma', 'Web Planning', 'UX/UI'],
  },
  {
    slug: 'enterprise-document-transfer-ux',
    title: '기업용 문서 전송 앱 UX 개선',
    subtitle: '반복적인 문서 전송 업무를 빠르게 처리하는 B2B 앱 UX',
    client: 'B2B 업무 솔루션 기업',
    categories: ['앱', 'UX/UI'],
    scope: 'B2B 앱 · 서비스 기획 · UX/UI',
    date: '2025-09-01',
    thumbnail: '/images/portfolio/enterprise-document-transfer-ux.png',
    thumbnailFit: 'contain',
    description: '기업의 반복적인 문서 전송 업무를 더 빠르고 간편하게 처리할 수 있도록 앱의 주요 사용자 흐름을 개선했습니다.',
    background: '문서 작성, 수신처 설정, 전송, 결과 확인 과정을 효율적으로 처리해야 하는 B2B 서비스였습니다. 기능이 많아도 사용 과정이 복잡해지지 않도록 화면 구조와 정보 우선순위를 재정리했습니다.',
    outcome: '문서 전송의 핵심 단계를 명확히 구분하고 반복 입력을 줄여, 업무 사용자가 빠르게 처리할 수 있는 구조로 개선했습니다.',
    featured: false,
    tasks: ['기존 서비스 사용자 흐름 분석', '문서 작성 및 전송 과정 재설계', '진입, 작성, 전송, 확인 화면 UX 개선', '업무 중심 정보구조 재정비', '주요 기능의 사용 우선순위 정의', '앱 전체 UX/UI 디자인', '반복 업무를 고려한 인터랙션 설계'],
    tools: ['Figma', 'App UX/UI'],
  },
  {
    slug: 'global-local-experience-service',
    title: '글로벌 로컬 경험 탐색 서비스',
    subtitle: '지역의 장소와 경험을 발견하는 글로벌 서비스 기획',
    client: '로컬 콘텐츠 서비스 기업',
    categories: ['앱'],
    scope: '앱 서비스 기획 · 글로벌 서비스',
    date: '2025-08-01',
    thumbnail: '/images/portfolio/global-local-experience-service.png',
    thumbnailFit: 'contain',
    description: '여행지의 유명 관광지가 아닌 현지의 장소와 경험을 발견하는 글로벌 로컬 콘텐츠 서비스를 기획했습니다.',
    background: '사용자가 지역 고유의 장소와 문화를 탐색하고 새로운 경험을 발견할 수 있도록 만드는 서비스였습니다. 국내 시장에서 시작하되 여러 국가로 확장할 수 있는 콘텐츠 구조와 서비스 흐름이 필요했습니다.',
    outcome: '특정 지역이나 국가에 종속되지 않는 콘텐츠 구조를 설계해, 이후 글로벌 확장과 지역별 운영이 가능한 서비스 기반을 마련했습니다.',
    featured: false,
    tasks: ['서비스 핵심 가치와 방향 정의', '주요 타깃 사용자 설정', '사용자 탐색 흐름 설계', '지역별 콘텐츠 구조 기획', '장소 및 경험 중심 정보구조 설계', '글로벌 확장을 고려한 콘텐츠 체계 구축', '초기 화면 및 주요 기능 정의'],
    tools: ['Service Planning', 'Global UX'],
  },
  {
    slug: 'preference-based-golf-matching-app',
    title: '취향 기반 골프 매칭 앱',
    subtitle: '취향과 활동 정보를 기반으로 연결하는 매칭 앱 UX/UI',
    client: '스포츠 커뮤니티 서비스 기업',
    categories: ['앱', 'UX/UI'],
    scope: '골프 매칭 앱 · UX/UI · 디자인 시스템',
    date: '2025-07-01',
    thumbnail: '/images/portfolio/preference-based-golf-matching-app.png',
    thumbnailFit: 'contain',
    description: '골프 사용자의 취향과 활동 정보를 기반으로 자연스러운 관계 형성을 돕는 매칭 서비스를 디자인했습니다.',
    background: '단순 추천을 넘어 골프 활동, 성향, 선호 조건을 바탕으로 적합한 사용자를 탐색하고 연결하는 서비스였습니다. 부담스럽지 않은 연결 경험과 신뢰도 있는 프로필 구조를 설계하는 데 집중했습니다.',
    outcome: '정보량이 많은 매칭 서비스를 직관적으로 탐색할 수 있도록 구조화하고, 화면 전반에 일관된 디자인 시스템을 적용했습니다.',
    featured: false,
    tasks: ['앱 전체 UX/UI 디자인', '사용자 가입 및 프로필 설정 흐름 설계', '취향과 조건 기반 매칭 과정 설계', '추천, 탐색, 연결 화면 디자인', '주요 인터랙션 및 상태 화면 설계', '앱 디자인 시스템 구축', '개발용 컴포넌트 및 산출물 정리'],
    tools: ['Figma', 'Design System', 'App UX/UI'],
  },
  {
    slug: 'expert-collaboration-platform',
    title: '전문가 협업 플랫폼 홈페이지',
    subtitle: '전문가와 기업의 연결 방식을 명확히 전달하는 홈페이지',
    client: '비즈니스 네트워크 플랫폼 기업',
    categories: ['웹', 'UX/UI', '퍼블리싱'],
    scope: '기업 홈페이지 · UX/UI · 퍼블리싱',
    date: '2025-06-01',
    thumbnail: '/images/portfolio/expert-collaboration-platform.png',
    thumbnailFit: 'contain',
    description: '전문가와 기업 간의 연결 방식과 서비스 가치를 명확하게 전달하는 기업 홈페이지를 제작했습니다.',
    background: '서비스의 전문성과 신뢰감을 방문자에게 짧은 시간 안에 전달해야 하는 홈페이지 프로젝트였습니다. 서비스 소개와 작업 방식, 핵심 가치를 자연스럽게 이해할 수 있도록 콘텐츠 흐름을 구성했습니다.',
    outcome: '복잡한 서비스 개념을 빠르게 이해할 수 있도록 정리하고, 전문성과 신뢰감을 중심으로 홈페이지를 완성했습니다.',
    featured: false,
    tasks: ['홈페이지 콘텐츠 구조 설계', '메인 페이지 UX/UI 디자인', '서비스 신뢰도 중심 정보구조 구성', '기업 및 전문가 대상 사용자 흐름 설계', 'PC, 태블릿, 모바일 반응형 디자인', '공통 컴포넌트 설계', '전체 홈페이지 퍼블리싱'],
    tools: ['Figma', 'Responsive Web', 'Publishing'],
  },
  {
    slug: 'saas-subscription-management',
    title: 'SaaS 구독 통합관리 웹 서비스',
    subtitle: '분산된 구독과 결제 정보를 관리하는 통합 대시보드',
    client: '정부지원사업 수행 스타트업',
    categories: ['웹', 'UX/UI'],
    scope: 'SaaS 관리 웹 · 서비스 기획 · UX/UI',
    date: '2025-05-01',
    thumbnail: '/images/portfolio/saas-subscription-management.png',
    thumbnailFit: 'contain',
    description: '여러 SaaS 서비스의 구독 현황과 결제 정보를 한 곳에서 확인하고 관리할 수 있는 통합 웹 서비스를 설계했습니다.',
    background: '기업과 개인이 사용하는 여러 구독형 서비스를 통합 관리해 비용과 관리 부담을 줄이는 서비스였습니다. 다양한 서비스 정보를 하나의 화면에서 비교하고 관리할 수 있도록 명확한 정보구조가 필요했습니다.',
    outcome: '분산된 구독 정보를 대시보드 중심으로 구성해, 사용자가 비용과 갱신 일정을 쉽게 관리할 수 있는 구조를 설계했습니다.',
    featured: false,
    tasks: ['SaaS 통합관리 웹 서비스 기획', '전체 정보구조 및 사용자 흐름 설계', '구독 현황 대시보드 UX/UI 디자인', '결제 이력 및 갱신 일정 관리 화면 설계', '서비스 등록 및 관리 기능 UX/UI 디자인', '반응형 웹 화면 설계', '개발 협업 및 디자인 산출물 관리', '정부지원사업 일정에 맞춘 프로젝트 납품'],
    tools: ['Figma', 'SaaS UX/UI', 'Responsive Web'],
  },
  {
    slug: 'global-commerce-app-redesign',
    title: '글로벌 커머스 앱 UX/UI 리디자인',
    subtitle: '상품 탐색부터 결제까지 구매 경험 전반 개선',
    client: '글로벌 유통·커머스 기업',
    categories: ['앱', 'UX/UI'],
    scope: '커머스 앱 · UX/UI 리디자인',
    date: '2025-04-01',
    thumbnail: '/images/portfolio/global-commerce-app-redesign.png',
    thumbnailFit: 'contain',
    description: '상품 탐색부터 장바구니, 결제, 주문 확인까지 커머스 앱의 핵심 구매 경험을 전반적으로 개선했습니다.',
    background: '기존 서비스의 주요 기능과 콘텐츠는 유지하면서도 사용자가 상품을 더 쉽게 발견하고 구매할 수 있도록 화면 구조와 시각적 완성도를 개선하는 프로젝트였습니다.',
    outcome: '상품 탐색과 구매 과정에서 필요한 정보를 명확히 구분하고, 주요 화면의 사용성과 시각적 일관성을 높였습니다.',
    featured: false,
    tasks: ['기존 커머스 앱 사용자 흐름 분석', '홈 및 인기 상품 화면 리디자인', '카테고리 및 사이드 메뉴 구조 개선', '상품 상세 및 장바구니 화면 디자인', '결제 과정 UX/UI 개선', '마이페이지 및 주문내역 화면 디자인', '문의 및 고객지원 화면 설계', 'Figma 컴포넌트 및 개발용 산출물 제작'],
    tools: ['Figma', 'Commerce UX/UI'],
  },
  {
    slug: 'multi-device-design-partnership',
    title: '모바일·워치 멀티디바이스 디자인 파트너십',
    subtitle: '다수 모바일 서비스의 UX/UI 품질을 관리한 디자인 파트너십',
    client: '모바일 헬스케어·기술 기업',
    categories: ['앱', 'UX/UI'],
    scope: '모바일 앱 · 워치 UI · 디자인 파트너십',
    date: '2025-03-01',
    thumbnail: '/images/portfolio/multi-device-design-partnership.png',
    thumbnailFit: 'contain',
    description: '모바일 앱과 워치 인터페이스를 포함한 여러 서비스의 UX/UI 디자인을 파트너십 형태로 지원했습니다.',
    background: '서로 다른 목적을 가진 다수 프로젝트를 일정 기간 지속적으로 수행한 디자인 파트너십입니다. 각 서비스의 특성은 유지하면서도 전반적인 품질 관리와 개발 전달 방식의 일관성을 확보해야 했습니다.',
    outcome: '다양한 디바이스와 서비스에 대응할 수 있는 디자인 프로세스를 구축하고, 여러 프로젝트에서 일관된 UX/UI 품질을 유지했습니다.',
    featured: false,
    tasks: ['프로젝트별 요구사항 및 UX 구조 정의', '모바일 앱 UX/UI 디자인', '스마트워치 인터페이스 디자인', '디바이스별 화면 밀도 및 인터랙션 설계', '모바일과 워치 간 사용 흐름 연결', '주요 인터랙션 및 상태 화면 설계', '프로젝트별 디자인 품질 관리', '개발 전달용 디자인 산출물 정리'],
    tools: ['Figma', 'Mobile UX/UI', 'Watch UI'],
  },
  {
    slug: 'senior-lifestyle-app',
    title: '시니어 생활지원 앱 서비스',
    subtitle: '시니어 사용자가 쉽게 이해하고 사용할 수 있는 앱 UX/UI',
    client: '시니어 라이프스타일 서비스 기업',
    categories: ['앱', 'UX/UI'],
    scope: '시니어 앱 · 서비스 기획 · UX/UI',
    date: '2024-08-01',
    thumbnail: '/images/portfolio/senior-lifestyle-app.png',
    thumbnailFit: 'contain',
    description: '시니어 사용자를 위한 앱 서비스로, 초기 서비스 구조부터 UX/UI 전반을 설계했습니다.',
    background: '디지털 서비스 사용에 익숙하지 않은 사용자도 주요 기능을 쉽게 이해하고 이용할 수 있어야 하는 프로젝트였습니다. 작은 화면에서도 정보를 명확하게 보여주고 콘텐츠와 인터페이스를 단순화하는 데 집중했습니다.',
    outcome: '사용자가 다음 행동을 쉽게 이해할 수 있도록 화면을 단순화하고, 가독성과 조작 편의성을 중심으로 서비스 경험을 설계했습니다.',
    featured: false,
    tasks: ['초기 서비스 아이디어 구체화', '서비스 주요 기능 및 범위 정의', '전체 정보구조 설계', '사용자 흐름 및 화면 구조 정의', '시니어 사용자 중심 UX 설계', '가독성을 고려한 앱 UI 디자인', '주요 화면 및 공통 컴포넌트 설계'],
    tools: ['Figma', 'Service Planning', 'App UX/UI'],
  },
  {
    slug: 'artist-content-platform',
    title: '아티스트 콘텐츠 플랫폼',
    subtitle: '예술가와 작품을 탐색하는 콘텐츠 중심 반응형 플랫폼',
    client: '문화·예술 콘텐츠 서비스 기업',
    categories: ['웹', 'UX/UI', '퍼블리싱'],
    scope: '콘텐츠 플랫폼 · 반응형 웹 · 퍼블리싱',
    date: '2023-10-01',
    thumbnail: '/images/portfolio/artist-content-platform.png',
    thumbnailFit: 'contain',
    description: '다양한 예술가와 작품을 한 곳에서 탐색할 수 있는 콘텐츠 중심 반응형 웹 플랫폼을 제작했습니다.',
    background: '여러 유형의 작품 정보를 콘텐츠 성격에 맞게 구성하고, 사용자가 자연스럽게 다양한 작업을 탐색할 수 있도록 설계하는 프로젝트였습니다. 이미지와 텍스트가 조화롭게 보이는 콘텐츠 중심 화면이 필요했습니다.',
    outcome: '콘텐츠마다 가진 시각적 특성을 살리면서도 사용자가 다양한 예술가와 작품을 자연스럽게 탐색할 수 있는 반응형 플랫폼을 구현했습니다.',
    featured: false,
    tasks: ['아티스트 및 작품 콘텐츠 구조 설계', '콘텐츠 중심 정보구조 정의', '사용자 탐색 흐름 설계', '반응형 웹 UX/UI 디자인', '목록 및 상세 페이지 화면 설계', '이미지 중심 레이아웃 구성', '전체 페이지 퍼블리싱'],
    tools: ['Figma', 'Responsive Web', 'Publishing'],
  },
];

const legacyPortfolioProjects: PortfolioProject[] = [
  {
    slug: 'issue-town', title: '이슈타운', subtitle: '운영 데이터 대시보드 구축', client: '이슈타운', categories: ['웹', 'UX/UI'], scope: '관리자 UI · 운영 데이터', date: '2023-01-01', thumbnail: '/images/project6.png', description: '실시간 지표 확인과 운영 이력 관리를 위한 관리자 대시보드 사례입니다.', background: '운영 현황을 빠르게 확인할 수 있도록 관리자 화면을 정리했습니다.', outcome: '운영 데이터 접근성을 개선했습니다.', featured: false, tasks: ['운영 대시보드 구성', '관리자 화면 설계'], legacy: true,
  },
  {
    slug: 'senior-place', title: '시니어 플레이스', subtitle: '현장 업무용 모바일 앱', client: '시니어 플레이스', categories: ['앱', 'UX/UI'], scope: '하이브리드 앱 · 운영 연동', date: '2024-01-01', thumbnail: '/images/project5.png', description: '현장 업무용 하이브리드 앱 사례입니다.', background: '모바일 환경의 업무 흐름을 정리했습니다.', outcome: '주요 업무 접근성을 높였습니다.', featured: false, tasks: ['모바일 앱 화면 설계', '운영 연동 구조 정리'], legacy: true,
  },
  {
    slug: 'dal-li', title: '달리', subtitle: '관리자·백오피스 시스템', client: '달리', categories: ['웹', 'UX/UI'], scope: '백오피스 · 운영 관리', date: '2023-01-01', thumbnail: '/images/project1.png', description: '검색과 승인, 상태 관리를 지원하는 관리자 시스템 사례입니다.', background: '반복 운영 업무를 위한 관리 화면을 설계했습니다.', outcome: '운영 프로세스 효율을 높였습니다.', featured: false, tasks: ['관리자 화면 설계', '운영 프로세스 정리'], legacy: true,
  },
];

export const portfolioArchive: PortfolioProject[] = [
  ...legacyPortfolioProjects,
  ...portfolioProjects,
];

export const getPortfolioProject = (slug: string) =>
  portfolioArchive.find((project) => project.slug === slug);
